import { Injectable } from '@angular/core';
import { ParamMap, Params, Router } from '@angular/router';

export type NavigationSource =
  | 'service-orders'
  | 'demand'
  | 'approvals'
  | 'agenda';

export interface NavigationContext {
  source: NavigationSource;
  returnUrl: string | null;
}

export const NAVIGATION_SOURCE_PARAM = 'navSource';
export const NAVIGATION_RETURN_PARAM = 'navReturn';

const SOURCE_LABELS: Record<NavigationSource, string> = {
  'service-orders': 'Ordens de Serviço',
  demand: 'Solicitação',
  approvals: 'Aprovações',
  agenda: 'Agenda de Serviços',
};

export function navigationContextQueryParams(
  source: NavigationSource,
  returnUrl: string,
): Params {
  return {
    [NAVIGATION_SOURCE_PARAM]: source,
    [NAVIGATION_RETURN_PARAM]: safeReturnUrlForSource(source, returnUrl),
  };
}

export function readNavigationContext(params: ParamMap): NavigationContext {
  const source = validNavigationSource(params.get(NAVIGATION_SOURCE_PARAM));
  const resolvedSource = source ?? 'service-orders';

  return {
    source: resolvedSource,
    returnUrl: safeReturnUrlForSource(
      resolvedSource,
      params.get(NAVIGATION_RETURN_PARAM),
    ),
  };
}

export function carriedNavigationContext(params: ParamMap): Params {
  const source = validNavigationSource(params.get(NAVIGATION_SOURCE_PARAM));
  const returnUrl = source
    ? safeReturnUrlForSource(source, params.get(NAVIGATION_RETURN_PARAM))
    : null;

  return {
    ...(source ? { [NAVIGATION_SOURCE_PARAM]: source } : {}),
    ...(returnUrl ? { [NAVIGATION_RETURN_PARAM]: returnUrl } : {}),
  };
}

export function navigationSourceLabel(source: NavigationSource): string {
  return SOURCE_LABELS[source];
}

export function splitManagementUrl(url: string): {
  path: string;
  queryParams: Record<string, string>;
} | null {
  const safeUrl = safeManagementUrl(url);
  if (!safeUrl) return null;

  const parsed = new URL(safeUrl, 'http://profinders.local');
  return {
    path: parsed.pathname,
    queryParams: Object.fromEntries(parsed.searchParams.entries()),
  };
}

export function safeManagementUrl(value: string | null): string | null {
  if (!value || value.includes('\\') || value.startsWith('//')) return null;

  try {
    const parsed = new URL(value, 'http://profinders.local');
    if (
      parsed.origin !== 'http://profinders.local' ||
      !parsed.pathname.startsWith('/gestao/')
    ) {
      return null;
    }

    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return null;
  }
}

function validNavigationSource(value: string | null): NavigationSource | null {
  if (
    value === 'service-orders' ||
    value === 'demand' ||
    value === 'approvals' ||
    value === 'agenda'
  ) {
    return value;
  }

  return null;
}

function safeReturnUrlForSource(
  source: NavigationSource,
  value: string | null,
): string | null {
  const safeUrl = safeManagementUrl(value);
  if (!safeUrl) return null;

  const expectedPrefix: Record<NavigationSource, string> = {
    'service-orders': '/gestao/ordens-servico',
    demand: '/gestao/solicitacoes',
    approvals: '/gestao/aprovacoes',
    agenda: '/gestao/agenda',
  };
  return safeUrl.startsWith(expectedPrefix[source]) ? safeUrl : null;
}

@Injectable({ providedIn: 'root' })
export class NavigationContextService {
  context(params: ParamMap): NavigationContext {
    return readNavigationContext(params);
  }

  carriedParams(params: ParamMap): Params {
    return carriedNavigationContext(params);
  }

  backLabel(params: ParamMap): string {
    return `Voltar para ${navigationSourceLabel(this.context(params).source)}`;
  }

  navigateBack(
    router: Router,
    params: ParamMap,
    fallback = '/gestao/ordens-servico',
  ): Promise<boolean> {
    return router.navigateByUrl(this.context(params).returnUrl ?? fallback);
  }
}
